import React, { useMemo, useState } from 'react';
import {
  ArrowRight, Plus, Edit2, Trash2, List, Target, SkipForward, AlertCircle, XCircle, ToggleLeft, ToggleRight, ChevronDown, ChevronUp, X
} from 'lucide-react';
import { htmlToPlainText } from './utils';

const NavigationPanel = ({
  selectedElementData,
  selectedElement,
  screens,
  currentScreenIndex,
  setScreens,
  navigationRules,
  setNavigationRules,
  setShowRuleEditorModal,
  setEditingRule,
  setConfirmDialog,
  setSelectedElement,
  setCurrentScreenIndex
}) => {
  // Gather ALL rules from all elements across all screens.
  // Rules are keyed by element.id in state, so if the same element_id appears
  // on more than one screen the naive traversal would emit each rule once per
  // screen. Dedup by (element.id, rule.id) — the rule itself is screen-agnostic.
  const allRulesWithContext = useMemo(() => {
    const rules = [];
    const seen = new Set();

    screens.forEach((screen, screenIdx) => {
      if (!screen.elements) return;

      screen.elements.forEach(element => {
        const elementRules = navigationRules[element.id] || [];
        if (elementRules.length === 0) return;

        elementRules.forEach((rule, ruleIdx) => {
          const dedupKey = `${element.id}::${rule.id ?? `idx${ruleIdx}`}`;
          if (seen.has(dedupKey)) return;
          seen.add(dedupKey);

          // Get better element name - try content (HTML), questionText, etc.
          const rawContent = element.content || element.questionText || element.data?.text || element.itemData?.text || element.text || element.label || '';
          const cleanName = htmlToPlainText(rawContent).substring(0, 50) || 'Unnamed Question';

          rules.push({
            ...rule,
            elementId: element.id,
            elementName: cleanName,
            elementType: element.type,
            screenIndex: screenIdx,
            screenName: screen.name || `Screen ${screenIdx + 1}`,
            ruleIndex: ruleIdx
          });
        });
      });
    });

    return rules;
  }, [screens, navigationRules]);

  // Split rules into selected element rules and other rules
  const selectedElementRules = useMemo(() => {
    return allRulesWithContext.filter(r => r.elementId === selectedElement);
  }, [allRulesWithContext, selectedElement]);

  const otherRules = useMemo(() => {
    return allRulesWithContext.filter(r => r.elementId !== selectedElement);
  }, [allRulesWithContext, selectedElement]);

  const totalRuleCount = allRulesWithContext.length;

  // Track which rule is expanded for detailed view
  const [expandedRuleId, setExpandedRuleId] = useState(null);

  // Get all question elements for looking up question text
  const allQuestionElements = useMemo(() => {
    const elements = [];
    screens.forEach((screen, screenIdx) => {
      if (!screen.elements) return;
      screen.elements.forEach(el => {
        if (el.type === 'question') {
          // Try multiple property paths for question text (same as RuleEditorModal)
          const rawText = el.content || el.questionText || el.data?.text || el.itemData?.text || el.text || el.label || '';
          const displayText = htmlToPlainText(rawText).substring(0, 80) || 'Question';
          elements.push({
            id: el.id,
            questionId: el.questionId,
            questionText: displayText,
            screenName: screen.name || `Screen ${screenIdx + 1}`
          });
        }
      });
    });
    return elements;
  }, [screens]);

  // Helper to get question text by element ID, frontend questionId, or backend question ID
  const getQuestionText = (id) => {
    if (!id) return 'Question';
    const strId = String(id);
    // Try matching by element id, frontend questionId, or backend database questionId
    const el = allQuestionElements.find(q => 
      q.id === id || 
      q.id === strId ||
      String(q.questionId) === strId ||
      q.questionId === id
    );
    if (el) {
      return truncateName(el.questionText, 50);
    }
    return `Question #${id}`;
  };

  // Helper to format condition text with better detail
  const formatCondition = (rule, verbose = false) => {
    const op = rule.operator === '==' ? '=' : rule.operator;
    if (rule.condition_type === 'answer_value') {
      return { type: 'Answer', detail: `${op} ${rule.value}` };
    }
    if (rule.condition_type === 'cumulative_score') {
      const qCount = rule.question_ids?.length || rule.backend_question_ids?.length || 0;
      return { type: 'Sum', detail: `of ${qCount} Q${qCount !== 1 ? 's' : ''} ${op} ${rule.value}` };
    }
    if (rule.condition_type === 'answer_combination') {
      const condCount = rule.conditions?.length || rule.backend_conditions?.length || 0;
      const logic = rule.logic === 'or' ? 'ANY' : 'ALL';
      return { type: 'Combo', detail: `${condCount} condition${condCount !== 1 ? 's' : ''} (${logic})` };
    }
    return { type: rule.condition_type, detail: `${op} ${rule.value}` };
  };

  // Helper to format action text and get action styling
  const getActionInfo = (rule) => {
    if (rule.action_type === 'skip_to') return {
      text: `Skip to ${rule.target_screen_name || 'Screen'}`,
      icon: SkipForward,
      color: 'text-primary-700',
      bg: 'bg-primary-50'
    };
    if (rule.action_type === 'show_alert') return {
      text: 'Show Alert',
      icon: AlertCircle,
      color: 'text-amber-700',
      bg: 'bg-amber-50'
    };
    if (rule.action_type === 'end_survey') return {
      text: 'End Survey',
      icon: XCircle,
      color: 'text-red-700',
      bg: 'bg-red-50'
    };
    return { text: rule.action_type, icon: ArrowRight, color: 'text-gray-700', bg: 'bg-gray-50' };
  };

  // Truncate element name for display
  const truncateName = (name, maxLength = 30) => {
    if (!name) return 'Element';
    const stripped = htmlToPlainText(name);
    if (stripped.length <= maxLength) return stripped;
    return stripped.substring(0, maxLength) + '...';
  };

  // Handle clicking on a rule to navigate to that element
  const handleRuleClick = (rule) => {
    if (rule.screenIndex !== currentScreenIndex) {
      setCurrentScreenIndex(rule.screenIndex);
    }
    setSelectedElement(rule.elementId);
  };

  // Render a single rule card - with expandable detail view
  const RuleCard = ({ rule, isHighlighted, showElementContext }) => {
    const condition = formatCondition(rule);
    const actionInfo = getActionInfo(rule);
    const ActionIcon = actionInfo.icon;
    const isExpanded = expandedRuleId === rule.id;
    
    const handleCardClick = (e) => {
      if (!isHighlighted) {
        handleRuleClick(rule);
      } else {
        // Toggle expansion
        setExpandedRuleId(isExpanded ? null : rule.id);
      }
    };

    // Get detailed condition info for expanded view
    const getExpandedConditionDetails = () => {
      if (rule.condition_type === 'answer_value') {
        const qIds = rule.question_ids || rule.backend_question_ids || [];
        return {
          title: 'Answer Value Condition',
          items: qIds.length > 0 ? [{
            label: 'Question',
            value: getQuestionText(qIds[0])
          }] : [],
          comparison: `Value ${rule.operator === '==' ? '=' : rule.operator} ${rule.value}`
        };
      }
      if (rule.condition_type === 'cumulative_score') {
        const qIds = rule.question_ids || rule.backend_question_ids || [];
        return {
          title: 'Cumulative Score Condition',
          items: qIds.map((id, i) => ({
            label: `Q${i + 1}`,
            value: getQuestionText(id)
          })),
          comparison: `Total ${rule.operator === '==' ? '=' : rule.operator} ${rule.value}`
        };
      }
      if (rule.condition_type === 'answer_combination') {
        const conditions = rule.conditions || rule.backend_conditions || [];
        return {
          title: `Answer Combination (${rule.logic === 'or' ? 'ANY' : 'ALL'} match)`,
          items: conditions.map((cond, i) => ({
            label: `Condition ${i + 1}`,
            value: `${getQuestionText(cond.question_id)} ${cond.operator === '==' ? '=' : cond.operator} ${cond.value}`
          })),
          comparison: null
        };
      }
      return { title: 'Condition', items: [], comparison: `${rule.operator} ${rule.value}` };
    };
    
    return (
      <div
        className={`rounded-lg border cursor-pointer transition-all overflow-hidden ${
          isHighlighted
            ? rule.enabled !== false
              ? 'border-blue-400 ring-1 ring-blue-200'
              : 'border-gray-300 opacity-70'
            : rule.enabled !== false
              ? 'border-gray-200 dark:border-gray-700 hover:border-gray-300'
              : 'border-gray-200 dark:border-gray-700 opacity-50'
        }`}
        onClick={handleCardClick}
      >
        {/* Header row with context */}
        <div className={`px-2.5 py-1.5 flex items-center justify-between ${
          isHighlighted ? 'bg-blue-50' : 'bg-gray-50 dark:bg-gray-900'
        }`}>
          <div className="flex items-center gap-2 min-w-0 flex-1">
            {showElementContext ? (
              <>
                <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium whitespace-nowrap flex-shrink-0 ${
                  rule.condition_type === 'answer_combination' 
                    ? 'bg-purple-100 text-purple-700 border border-purple-200' 
                    : rule.condition_type === 'cumulative_score'
                    ? 'bg-orange-100 text-orange-700 border border-orange-200'
                    : 'bg-primary-100 text-primary-700 border border-primary-200'
                }`}>
                  {rule.condition_type === 'answer_combination' 
                    ? `${(rule.conditions?.length || rule.backend_conditions?.length || 0)} Questions`
                    : rule.condition_type === 'cumulative_score'
                    ? `Sum of ${(rule.question_ids?.length || rule.backend_question_ids?.length || 0)}`
                    : 'Answer'}
                </span>
                <span className="text-xs font-medium text-gray-700">
                  {actionInfo.text}
                </span>
              </>
            ) : (
              <span className={`text-xs font-semibold ${isHighlighted ? 'text-primary-700 dark:text-primary-400' : 'text-gray-600 dark:text-gray-400'}`}>
                Rule {rule.ruleIndex + 1}
              </span>
            )}
            {isHighlighted && (
              <span className="text-gray-400">
                {isExpanded ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
              </span>
            )}
          </div>
          {isHighlighted && (
            <div className="flex items-center gap-1 flex-shrink-0">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  const updatedRules = [...navigationRules[selectedElement]];
                  updatedRules[rule.ruleIndex] = { ...rule, enabled: rule.enabled === false ? true : false };
                  setNavigationRules({
                    ...navigationRules,
                    [selectedElement]: updatedRules
                  });
                }}
                className={`p-1 rounded transition-colors ${rule.enabled !== false ? 'text-primary-600 hover:bg-primary-50' : 'text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'}`}
                title={rule.enabled !== false ? 'Disable rule' : 'Enable rule'}
              >
                {rule.enabled !== false ? <ToggleRight className="w-4 h-4" /> : <ToggleLeft className="w-4 h-4" />}
              </button>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setEditingRule(rule);
                  setShowRuleEditorModal(true);
                }}
                className="p-1 text-gray-500 hover:text-primary-600 hover:bg-primary-50 dark:hover:bg-primary-900/20 rounded transition-colors"
                title="Edit rule"
              >
                <Edit2 className="w-3.5 h-3.5" />
              </button>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setConfirmDialog({
                    isOpen: true,
                    title: 'Delete Rule',
                    message: 'Are you sure you want to delete this navigation rule?',
                    type: 'danger',
                    onConfirm: () => {
                      const updatedRules = navigationRules[selectedElement].filter((_, i) => i !== rule.ruleIndex);
                      setNavigationRules({
                        ...navigationRules,
                        [selectedElement]: updatedRules
                      });
                      setConfirmDialog(prev => ({ ...prev, isOpen: false }));
                    }
                  });
                }}
                className="p-1 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
                title="Delete rule"
              >
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </div>
          )}
        </div>
        
        {/* Collapsed view - Condition and Action summary */}
        {!isExpanded && (
          <div className="px-2.5 py-2 bg-white dark:bg-gray-800 space-y-1.5">
            <div className="flex items-center gap-2">
              <span className="text-[10px] font-semibold text-gray-500 uppercase w-6">If</span>
              <div className="flex items-center gap-1.5">
                <span className="text-xs px-1.5 py-0.5 bg-purple-50 text-purple-700 rounded font-medium">
                  {condition.type}
                </span>
                <span className="text-xs text-gray-700">{condition.detail}</span>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-[10px] font-semibold text-gray-500 uppercase w-6">→</span>
              <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-xs font-medium ${actionInfo.bg} ${actionInfo.color}`}>
                <ActionIcon className="w-3 h-3" />
                {actionInfo.text}
              </span>
            </div>
          </div>
        )}

        {/* Expanded view - Full details */}
        {isExpanded && isHighlighted && (
          <div className="px-3 py-3 bg-white dark:bg-gray-800 border-t border-blue-100 space-y-3">
            {(() => {
              const details = getExpandedConditionDetails();
              return (
                <>
                  <div>
                    <p className="text-[10px] font-semibold text-purple-700 uppercase mb-2">{details.title}</p>
                    {details.items.length > 0 && (
                      <div className="space-y-1.5 mb-2">
                        {details.items.map((item, i) => (
                          <div key={i} className="flex items-start gap-2 text-xs">
                            <span className="text-gray-500 font-medium shrink-0">{item.label}:</span>
                            <span className="text-gray-800 break-words">{item.value}</span>
                          </div>
                        ))}
                      </div>
                    )}
                    {details.comparison && (
                      <div className="inline-flex items-center gap-1 px-2 py-1 bg-purple-50 text-purple-700 rounded text-xs font-medium">
                        {details.comparison}
                      </div>
                    )}
                  </div>

                  <div className="border-t border-gray-100 dark:border-gray-700 pt-2">
                    <p className="text-[10px] font-semibold text-primary-700 uppercase mb-2">Action</p>
                    <div className={`inline-flex items-center gap-1.5 px-2 py-1 rounded text-xs font-medium ${actionInfo.bg} ${actionInfo.color}`}>
                      <ActionIcon className="w-3.5 h-3.5" />
                      {actionInfo.text}
                    </div>
                    {rule.action_type === 'skip_to' && rule.target_screen_index !== undefined && (
                      <p className="text-[10px] text-gray-500 mt-1">Screen index: {rule.target_screen_index + 1}</p>
                    )}
                  </div>

                  {rule.enabled === false && (
                    <div className="bg-amber-50 border border-amber-200 rounded px-2 py-1.5 text-xs text-amber-700">
                      ⚠️ This rule is currently disabled
                    </div>
                  )}
                </>
              );
            })()}
          </div>
        )}
      </div>
    );
  };

  // Check if selected element can have rules (question or button with skip action)
  const canSelectedElementHaveRules = selectedElementData && (
    selectedElementData.type === 'question' || 
    (selectedElementData.type === 'button' && selectedElementData.action === 'skip')
  );

  return (
    <div className="p-3 space-y-3">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <ArrowRight className="w-4 h-4 text-primary-600" />
          <h3 className="text-sm font-semibold text-gray-900">Navigation Rules</h3>
        </div>
        <span className="text-xs text-gray-500">{totalRuleCount} rule{totalRuleCount !== 1 ? 's' : ''}</span>
      </div>

      {/* Add Rule Button */}
      <button
        className="w-full px-3 py-2 text-xs bg-primary-600 hover:bg-primary-700 text-white rounded-lg font-medium transition-colors flex items-center justify-center"
        onClick={() => setShowRuleEditorModal(true)}
      >
        <Plus className="w-3.5 h-3.5 mr-1.5" />
        Add Rule
      </button>

      {/* Simple flat list of all rules */}
      {totalRuleCount === 0 ? (
        <p className="text-xs text-gray-500 text-center py-4">
          No rules yet. Add a rule to control survey navigation.
        </p>
      ) : (
        <div className="space-y-2">
          {allRulesWithContext.map((rule, index) => (
            <RuleCard 
              key={`rule-${rule.elementId}-${rule.ruleIndex}-${index}`} 
              rule={rule} 
              isHighlighted={rule.elementId === selectedElement}
              showElementContext={true}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default NavigationPanel;
